import { statsService } from './statsService';
import { dbManager } from './db_manager';
import { getLocalISOString } from '../utils/timezoneUtils';

// Study & Reading routes monitored for active time and consistency
const STUDY_ROUTES = [
  '/flashcards',
  '/writing',
  '/pronunciation',
  '/grammar',
  '/active-voice',
  '/reader',
  '/review-errors',
  '/librirui-reader',
  '/roadmap',
  '/listening'
];

class ActiveStudyTrackerService {
  private activeSessionSeconds: number = 0;
  private lastActivityTimestamp: number = Date.now();
  private lastTickTimestamp: number = performance.now();
  private timerId: any = null;
  private currentPath: string = '';
  private currentUserId: string | null = null;
  private currentLangCode: string = 'all';
  private isInitialized: boolean = false;
  private IDLE_TIMEOUT_MS = 30000; // 30 seconds without interaction = IDLE freeze

  /**
   * Initialize event listeners and monotonic timer
   */
  public init(): void {
    if (typeof window === 'undefined' || this.isInitialized) return;
    this.isInitialized = true;

    this.currentPath = window.location.pathname.toLowerCase();
    this.lastActivityTimestamp = Date.now();
    this.lastTickTimestamp = performance.now();

    // Register passive event listeners for presence detection
    const updateActivity = () => {
      if (Date.now() - this.lastActivityTimestamp >= this.IDLE_TIMEOUT_MS) {
        console.log('[ActiveStudyTracker] Resuming active study stopwatch after user interaction.');
        this.lastTickTimestamp = performance.now();
      }
      this.lastActivityTimestamp = Date.now();
    };

    const events = ['mousemove', 'keydown', 'keyup', 'input', 'touchstart', 'pointerdown', 'scroll', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Handle visibility and page unload with keepalive/beacon flush
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });

    window.addEventListener('beforeunload', () => {
      this.flushBeacon();
    });

    // Stopwatch tick loop (runs every 500ms for high precision)
    this.timerId = setInterval(() => {
      this.tick();
    }, 500);
  }

  /**
   * Triggered on route transition
   */
  public onRouteChange(newPathname: string, userId?: string, langCode?: string): void {
    const cleanPath = newPathname.toLowerCase();

    if (this.isStudyRoute(this.currentPath) && !this.isStudyRoute(cleanPath)) {
      this.flush();
    }

    this.currentPath = cleanPath;
    if (userId) this.currentUserId = userId;
    
    // Extract route-based language code if present (e.g. /grammar/fr, /writing/de)
    const routeLang = this.extractLangFromPath(cleanPath);
    const targetLangToSet = langCode || routeLang;

    if (targetLangToSet && targetLangToSet !== 'all' && targetLangToSet !== this.currentLangCode) {
      this.flush();
      this.currentLangCode = targetLangToSet.split('-')[0].toLowerCase();
    }

    if (this.isStudyRoute(cleanPath)) {
      this.logConsistencySession();
    }
  }

  /**
   * Helper to extract 2-letter ISO language code from URL path
   */
  private extractLangFromPath(path: string): string | null {
    if (!path) return null;
    const parts = path.toLowerCase().split('/').filter(Boolean);
    const validCodes = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'zh', 'ru', 'ar', 'hi', 'ko', 'tr', 'nl', 'pl', 'sv', 'fi', 'da', 'no', 'cs', 'el', 'he', 'hu', 'ro', 'sk', 'th', 'uk', 'vi', 'bg', 'hr', 'sr', 'ca', 'gl', 'eu', 'id', 'ms'];
    for (const p of parts) {
      const code = p.split('-')[0];
      if (validCodes.includes(code)) return code;
    }
    return null;
  }

  /**
   * Set user context for logging
   */
  public setUserContext(userId: string | null, langCode?: string): void {
    if (userId) this.currentUserId = userId;
    if (langCode && langCode !== 'all') {
      const clean = langCode.split('-')[0].toLowerCase();
      if (clean && clean !== this.currentLangCode) {
        this.flush();
        this.currentLangCode = clean;
      }
    }
  }

  /**
   * Check if pathname is a study route
   */
  public isStudyRoute(pathname: string = this.currentPath): boolean {
    const path = (pathname || '').toLowerCase();
    return STUDY_ROUTES.some(route => path.startsWith(route));
  }

  /**
   * Monotonic Stopwatch Tick
   */
  private tick(): void {
    const nowPerf = performance.now();
    const elapsedSec = (nowPerf - this.lastTickTimestamp) / 1000;
    this.lastTickTimestamp = nowPerf;

    if (!this.isStudyRoute(this.currentPath)) return;

    // Check 30s idle threshold
    const isUserActive = (Date.now() - this.lastActivityTimestamp) < this.IDLE_TIMEOUT_MS;

    if (isUserActive) {
      this.activeSessionSeconds += elapsedSec;

      // Auto-flush every 20 seconds of accumulated active time
      if (this.activeSessionSeconds >= 20) {
        this.flush();
      }
    } else {
      // User is idle (>30s). Freeze and flush any pending active seconds.
      if (this.activeSessionSeconds > 0) {
        console.log('[ActiveStudyTracker] Stopwatch frozen after 30s of idle.');
        this.flush();
      }
    }
  }

  /**
   * Safe language resolver following strict context hierarchy.
   * Returns null if no valid language code is resolved (PROHIBITED from defaulting to 'en' or 'all').
   */
  private resolveCleanLang(providedLang?: string): string | null {
    const isValidIso = (code?: string | null): boolean => {
      if (!code || typeof code !== 'string') return false;
      const clean = code.trim().split('-')[0].split('_')[0].toLowerCase();
      return clean.length >= 2 && clean.length <= 5 && clean !== 'all' && clean !== 'undefined' && clean !== 'null';
    };

    // 1. Direct parameter or current context
    if (isValidIso(providedLang)) return providedLang!.trim().split('-')[0].toLowerCase();
    if (isValidIso(this.currentLangCode)) return this.currentLangCode.trim().split('-')[0].toLowerCase();

    // 2. Route path checking
    const pathLang = this.extractLangFromPath(this.currentPath || (typeof window !== 'undefined' ? window.location.pathname : ''));
    if (isValidIso(pathLang)) return pathLang!.trim().split('-')[0].toLowerCase();

    // 3. Global study context / localStorage
    if (typeof localStorage !== 'undefined') {
      const stored1 = localStorage.getItem('libritui_learning_lang');
      if (isValidIso(stored1)) return stored1!.trim().split('-')[0].toLowerCase();

      const stored2 = localStorage.getItem('current_language');
      if (isValidIso(stored2)) return stored2!.trim().split('-')[0].toLowerCase();
    }

    return null; // Strict rule: If language is not validly resolved, do NOT default to 'en'.
  }

  /**
   * Flush accumulated active minutes to D1
   */
  public flush(): void {
    if (this.activeSessionSeconds <= 0) return;

    const secondsToFlush = this.activeSessionSeconds;
    this.activeSessionSeconds = 0;

    const minutesFloat = Math.round((secondsToFlush / 60.0) * 100) / 100;
    if (minutesFloat <= 0) return;

    const userId = this.currentUserId || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);
    if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest') return;

    const cleanLang = this.resolveCleanLang();
    if (!cleanLang) return;
    const todayStr = getLocalISOString().split('T')[0];

    try {
      console.log(`[ActiveStudyTracker] Flushing ${secondsToFlush.toFixed(1)}s (${minutesFloat} min) for lang: ${cleanLang}`);
      
      // 1. Send to statsService for user_daily_logs.estimated_minutes
      statsService.increment('estimated_minutes' as any, userId, minutesFloat, {
        langCode: cleanLang
      });

      // 2. Direct pipeline log to logs_tempo_estimado (UPSERT)
      dbManager.execute({
        action: 'increment_stat',
        table: 'logs_tempo_estimado',
        user_id: userId,
        field: 'estimated_minutes',
        amount: minutesFloat,
        data: {
          field: 'estimated_minutes',
          amount: minutesFloat,
          local_date: todayStr,
          lang_code: cleanLang,
          target_lang: cleanLang,
          minutes: minutesFloat
        }
      }).catch(() => {});
    } catch (e) {
      console.warn('[ActiveStudyTracker] Flush failed:', e);
    }
  }

  /**
   * Beacon / keepalive flush for tab unload
   */
  private flushBeacon(): void {
    if (this.activeSessionSeconds <= 0) return;
    const seconds = this.activeSessionSeconds;
    this.activeSessionSeconds = 0;

    const minutesFloat = Math.round((seconds / 60.0) * 100) / 100;
    if (minutesFloat <= 0) return;

    const userId = this.currentUserId || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);
    if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest') return;

    const cleanLang = this.resolveCleanLang();
    if (!cleanLang) return;
    const todayStr = getLocalISOString().split('T')[0];

    const payload = JSON.stringify({
      action: 'increment_stat',
      table: 'logs_tempo_estimado',
      user_id: userId,
      field: 'estimated_minutes',
      amount: minutesFloat,
      data: {
        field: 'estimated_minutes',
        amount: minutesFloat,
        local_date: todayStr,
        lang_code: cleanLang,
        target_lang: cleanLang,
        minutes: minutesFloat
      }
    });

    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/db', payload);
      } else if (typeof fetch !== 'undefined') {
        fetch('/api/db', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true
        }).catch(() => {});
      }
    } catch (e) {}
  }

  /**
   * Start or update active study session language
   */
  public startSession(langCode?: string): void {
    this.init();
    if (langCode && langCode !== 'all') {
      const clean = langCode.split('-')[0].split('_')[0].toLowerCase();
      if (clean && clean.length >= 2) {
        this.currentLangCode = clean;
      }
    }
    this.lastActivityTimestamp = Date.now();
    this.lastTickTimestamp = performance.now();
  }

  /**
   * Get currently tracked language code
   */
  public getCurrentLang(): string {
    return this.currentLangCode && this.currentLangCode !== 'all' ? this.currentLangCode : '';
  }

  /**
   * Log precision result for individual exercises
   */
  public logExercisePrecision(isCorrect: boolean, userId?: string, targetLang?: string): void {
    const uId = userId || this.currentUserId || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);
    if (!uId) return;

    // Usa o targetLang passado ou resgata o idioma ativo da sessão (que já está 'fr')
    const cleanLang = this.resolveCleanLang(targetLang) || this.resolveCleanLang(this.currentLangCode);

    if (!cleanLang || cleanLang === 'all' || cleanLang.length < 2) {
      return;
    }

    const todayStr = getLocalISOString().split('T')[0];

    dbManager.execute({
      action: 'log_precision_exercise',
      table: 'logs_taxa_precisao',
      user_id: uId,
      target_lang: cleanLang, // Nível raiz
      data: {
        user_id: uId,
        target_lang: cleanLang, // Dentro de data
        is_correct: isCorrect,
        activity_date: todayStr,
        local_date: todayStr
      }
    }).catch((err) => console.warn('[ActiveStudyTracker] Erro ao salvar precisão:', err));
  }

  /**
   * Log consistency session for user daily presence
   */
  public logConsistencySession(userId?: string, targetLang?: string): void {
    const uId = userId || this.currentUserId || (typeof localStorage !== 'undefined' ? localStorage.getItem('libritui_user_id') || undefined : undefined);
    if (!uId) return;

    const cleanLang = this.resolveCleanLang(targetLang) || this.resolveCleanLang(this.currentLangCode);

    if (!cleanLang || cleanLang === 'all' || cleanLang.length < 2) {
      return;
    }

    const todayStr = getLocalISOString().split('T')[0];

    dbManager.execute({
      action: 'log_consistency_session',
      table: 'logs_consistencia',
      user_id: uId,
      target_lang: cleanLang, // Nível raiz
      data: {
        user_id: uId,
        target_lang: cleanLang, // Dentro de data
        activity_date: todayStr,
        local_date: todayStr
      }
    }).catch((err) => console.warn('[ActiveStudyTracker] Erro ao salvar consistência:', err));
  }
}

export const activeStudyTracker = new ActiveStudyTrackerService();

if (typeof window !== 'undefined') {
  (window as any).activeStudyTracker = activeStudyTracker;
  activeStudyTracker.init();
}
